import { Intro, StatStrip, Friction, HeroStat, StackedBar, Callout, Takeaway, SectionLead, Disclaimer, Shot } from './components.jsx';

export const meta = {
  slug: 'mivi',
  metaTitle: 'How MIVI Saved $273K in Support Costs | Account Editor',
  metaDescription:
    'Learn how MIVI deflected 58,242 post-purchase support actions, saving roughly $273K while serving 31,269 orders without a support ticket.',
  brand: 'MIVI',
  industry: 'Consumer Audio · Shopify Plus',
  statNum: '~$273K',
  statDesc: 'Support cost avoided · 83.5% self-serve adoption',
  detail:
    'How MIVI deflected 58,242 post-purchase support actions — invoices, cancellations, and address fixes — saving roughly $273K and serving 31,269 orders without a support ticket.',
  quote:
    '"GST invoices, cancellations, address fixes — at our volume these are not edge cases, they are the queue. Account Editor moved that entire queue onto the order page."',
  author: 'MIVI Team',
  role: 'Consumer Audio · India',
  img: 'MIVI',
  hero: 'MIVI+Case+Study',
  image: '/images/case-studies/mivi-case-study.webp',
  readTime: '5 min read',
  date: 'May 25, 2026'
};

export default function Content() {
  return (
    <>
      <Intro figure="58,000+" figureLabel="questions, self-answered">
        Every order ends with a question — moments that usually become support tickets. With
        Account Editor, MIVI's customers resolved them on their own, in seconds.
      </Intro>

      <StatStrip
        items={[
          { num: '31,269', label: 'unique orders self-served' },
          { num: '83.5%', label: 'of orders used Account Editor' },
          { num: '58,242', label: 'self-service actions' },
          { num: '~$273K', label: 'est. support cost avoided', highlight: true },
        ]}
        period="Oct 2025 – May 2026 · ~7 months"
      />

      <SectionLead eyebrow="The friction" title="The questions that pile up after checkout.">
        At MIVI's scale on Shopify Plus, three recurring post-purchase requests were generating
        a relentless support queue — and most of them were entirely automatable.
      </SectionLead>

      <Friction
        question="I need a proper tax invoice for my order — where do I get it?"
        oldWay="Email support, wait for a reply, get the GST invoice forwarded one ticket at a time. At thousands of orders a week, this scales as a queue, not a workflow."
        newWay="Customer downloads the GST invoice directly from the order page — including a custom bulk-download flow for multiple orders at once."
        callout="Customers did this themselves <strong>56,937</strong> times."
      />

      <Friction
        question="I ordered by mistake — I need to cancel before it ships."
        oldWay="Email support and race the warehouse. Cancellations are time-sensitive and email never beats fulfilment."
        newWay="Customer cancels on the order page within defined business rules — no ticket, no waiting, before pick-and-pack."
        callout="Customers self-cancelled <strong>1,205</strong> orders."
      />

      <Friction
        question="My address and contact details are wrong — fix them before delivery."
        oldWay="Email support and race against the clock, hoping the change lands before the order is picked and packed."
        newWay="Update contact info and shipping address directly on the order — corrected at the source, no support thread needed."
        callout="Customers fixed their own details <strong>100</strong> times."
      />

      <Shot
        src="/images/case-studies/mivi-order-status-page.webp"
        alt="MIVI customer's order status page with self-serve actions: edit address, edit items, cancel order, and download invoice"
        caption="Invoices, cancellations, and address fixes — all self-served from the customer's order status page."
      />

      <SectionLead eyebrow="The result" title="58,242 requests resolved before they reached a human.">
        83.5% of orders used Account Editor. 31,269 unique orders served without a support touch. The
        deflection rate translates directly into avoided support cost.
      </SectionLead>

      <HeroStat
        num="~$273K"
        label="estimated support cost avoided"
        period="Oct 2025 – May 2026"
        breakdown={[
          { label: 'Staff time saved', num: '$39,973', note: '58,242 actions × 2 min @ $20.59/hr ≈ 1,941 hrs' },
          { label: 'Support tickets avoided', num: '$232,968', note: '58,242 actions × $4 avoided cost per contact' },
        ]}
      />

      <StackedBar
        title="The full self-service picture"
        total="58,242"
        totalLabel="total actions"
        segments={[
          { label: 'Invoice downloads', value: 56937, tone: 'a' },
          { label: 'Self-cancellations', value: 1205, tone: 'b' },
          { label: 'Address & contact fixes', value: 100, tone: 'c' },
        ]}
      />

      <SectionLead eyebrow="Built for MIVI's scale" title="At tens of thousands of invoices, one at a time doesn't scale.">
        Most apps hand you one invoice at a time. MIVI needed bulk retrieval to handle India's GST
        workflow at volume, so Account Editor built a custom bulk-invoice-download flow for their order
        page — the kind of fit-for-purpose feature that turns thousands of tickets into a single
        customer action.
      </SectionLead>

      <Callout>
        Invoices pulled on demand. Orders cancelled in seconds. Addresses fixed at the source.
        Three of the most common support reasons — handled without support.
      </Callout>

      <Takeaway footnote="Measured across the MIVI deployment · Oct 2025 – May 2026">
        <strong>58,242</strong> actions, zero support tickets. <strong>83.5%</strong> of orders
        used Account Editor. <strong>~$273K</strong> in support cost prevented — without growing the
        team.
      </Takeaway>

      <Disclaimer>
        Figures cover Oct 11, 2025 – May 25, 2026. Estimated support cost avoided is modelled
        from 58,242 measured self-service actions: $39,973 staff time (2 minutes per action at
        $20.59/hr ≈ 1,941 hours) plus $232,968 in avoided support contacts (58,242 × $4 per
        contact). Action counts are measured directly from the Account Editor deployment.
      </Disclaimer>
    </>
  );
}
